/**
 * Validates the locale segment on client-side navigation and normalizes URLs plus cookies when needed.
 * A locale is considered valid when the path prefix matches `/{country}-{lang}` where `country` exists in
 * the `getCountryLanguages()` map and `lang` is one of that country's `availableLanguages` entries.
 * Skips Storyblok preview and well-known assets because the server middleware already sets the locale there.
 * Example: a visit through a malformed link to `/de-xx/about` (invalid language) is corrected to `/de-de/about` while updating if needed.
 *
 * @param to Destination route that is being entered.
 * @returns Redirect navigation when the locale is invalid, otherwise nothing.
 */
export default defineNuxtRouteMiddleware((to) => {
    const segment = to.path.split('/')[1] || '';

    if (segment.toLowerCase() === 'api' || to.fullPath.includes('.well-known') || to.fullPath.includes('favicon.ico')) {
        return;
    }

    const { languageCookie, countryCookie } = useCountryLanguageCookies();

    let countryLanguages: CountryLanguages;
    try {
        const draftMode = useDraftMode();
        const mode = draftMode.isDraftModeEnabled.value ? StoryblokVersion.Draft : StoryblokVersion.Published;
        countryLanguages = getCountryLanguages(mode);
    } catch {
        // fail gracefully
        // on first entry countryLanguages is not initialized yet
        // but we don't need this middleware on first entry because the server middleware already handles the locale
        // so we can just ignore the error and return
        return;
    }

    const pathWithoutSegment = to.path.replace(`/${segment}`, '');
    const [countryCode, langCode] = segment.split('-');

    const availableCountries = Object.keys(countryLanguages);
    const isValidCountry = availableCountries.includes(countryCode);
    const isValidLang = isValidCountry && countryLanguages[countryCode].availableLanguages.includes(langCode);
    const validCookieCountry =
        countryCookie.value && availableCountries.includes(countryCookie.value) ? countryCookie.value : null;
    const validCookieLang =
        languageCookie.value &&
        validCookieCountry &&
        countryLanguages[validCookieCountry].availableLanguages.includes(languageCookie.value)
            ? languageCookie.value
            : null;

    if (isValidCountry && isValidLang) {
        countryCookie.value = countryCode;
        languageCookie.value = langCode;
        return;
    }

    const fallbackCountry = isValidCountry ? countryCode : validCookieCountry || availableCountries[0];
    const fallbackLang =
        (validCookieCountry === fallbackCountry && validCookieLang) ||
        countryLanguages[fallbackCountry].defaultLanguage;

    countryCookie.value = fallbackCountry;
    languageCookie.value = fallbackLang;

    const fallbackPath = `/${fallbackCountry}-${fallbackLang}${pathWithoutSegment}`;
    log.debug(`[validateLocale] redirecting (fallbackCountry: ${fallbackCountry}, fallbackLang: ${fallbackLang})`);
    return navigateTo(fallbackPath, { replace: true });
});
